import { refreshChat } from "./app-chat.ts";
import { reloadLogs } from "./app-logs.ts";
import {
  startLogsPolling,
  stopLogsPolling,
  startDebugPolling,
  stopDebugPolling,
  startLiveSessionsPolling,
  stopLiveSessionsPolling,
  startLlmStatsCapacityPolling,
  stopLlmStatsCapacityPolling,
  startLocalLlmPolling,
  stopLocalLlmPolling,
  startAgentsPolling,
  stopAgentsPolling,
  startNodesPolling,
  stopNodesPolling,
  startSubagentsPolling,
  stopSubagentsPolling,
  syncLocalLlmPolling,
  syncChatPolling,
} from "./app-polling.ts";
import { scheduleChatScroll, scheduleLogsScroll } from "./app-scroll.ts";
import type { OpenClawApp } from "./app.ts";
import { loadAgentIdentities, loadAgentIdentity } from "./controllers/agent-identity.ts";
import { loadAgentSkills } from "./controllers/agent-skills.ts";
import { loadSessionsForAgentsImport } from "./controllers/agents-import-from-chat.ts";
import { resolveAgentsTabAgentId } from "./controllers/agents-selection.ts";
import { loadAgents } from "./controllers/agents.ts";
import { loadChannels } from "./controllers/channels.ts";
import { loadConfig, loadConfigSchema } from "./controllers/config.ts";
import { loadCronJobs, loadCronStatus } from "./controllers/cron.ts";
import { loadDebug } from "./controllers/debug.ts";
import { loadDevBuildInfo } from "./controllers/developer.ts";
import { loadDevices } from "./controllers/devices.ts";
import { loadEmployeesData } from "./controllers/employees.js";
import { loadExecApprovals } from "./controllers/exec-approvals.ts";
import { hydrateFleetRolesFromConfig } from "./controllers/fleet-roles.ts";
import {
  loadFleetWorkPlanBranches,
  loadFleetWorkPlanDetail,
  loadFleetWorkPlans,
  syncFleetWorkPlanPolling,
} from "./controllers/fleet-work-plan.ts";
import { loadGoogleDriveStatus, type GoogleDriveHost } from "./controllers/google-drive.ts";
import { loadLiveSessions } from "./controllers/live-sessions.ts";
import { loadLocalResources } from "./controllers/llm-stats-capacity.ts";
import { loadLlmStats } from "./controllers/llm-stats.ts";
import { refreshLocalLlm } from "./controllers/local-llm.ts";
import { loadLogs } from "./controllers/logs.ts";
import { loadNodes } from "./controllers/nodes.ts";
import { loadPresence } from "./controllers/presence.ts";
import { loadSessions } from "./controllers/sessions.ts";
import { loadSkills } from "./controllers/skills.ts";
import { loadWorkforceUi } from "./controllers/workforce-ui.ts";
import type { HelpTopicId } from "./help/help-content.ts";
import { helpTopicFromPath, pathForHelpTopic } from "./help/help-routes.ts";
import { isLocalLlmViewActive } from "./local-llm/view-active.ts";
import {
  inferBasePathFromPathname,
  isFleetConsoleTab,
  normalizeBasePath,
  normalizePath,
  pathForTab,
  pathForEmployeeDetail,
  tabFromPath,
  employeeIdFromPath,
  type Tab,
} from "./navigation.ts";
import { saveSettings, type UiSettings } from "./storage.ts";
import { startThemeTransition, type ThemeTransitionContext } from "./theme-transition.ts";
import { resolveTheme, type ResolvedTheme, type ThemeMode } from "./theme.ts";
import type { AgentsListResult } from "./types.ts";

type SettingsHost = {
  settings: UiSettings;
  password?: string;
  theme: ThemeMode;
  themeResolved: ResolvedTheme;
  applySessionKey: string;
  sessionKey: string;
  tab: Tab;
  helpTopic: HelpTopicId;
  connected: boolean;
  chatHasAutoScrolled: boolean;
  logsAtBottom: boolean;
  eventLog: unknown[];
  eventLogBuffer: unknown[];
  eventLogBySession: Record<string, unknown[]>;
  basePath: string;
  agentsList?: AgentsListResult | null;
  agentsSelectedId?: string | null;
  agentsPanel?: "overview" | "files" | "tools" | "skills" | "channels" | "cron" | "workplan";
  themeMedia: MediaQueryList | null;
  themeMediaHandler: ((event: MediaQueryListEvent) => void) | null;
  pendingGatewayUrl?: string | null;
  setSelectedEmployee?: (employeeId: string | null) => void;
};

/**
 * JAISIU-XXXX — central guard for rehydrating persisted session keys.
 *
 * The webchat client persists session keys in several places
 * (`settings.sessionKey`, `settings.lastActiveSessionKey`, the
 * `chatOpenTabs` array, and the `?session=` URL param). The previous
 * fix at `app.ts` line 627 only protected the `sessionKey` state on
 * initial render, so a fork could still sneak back in through the
 * other paths and the next operator message would land in a
 * sidetracked conversation.
 *
 * The regex and helpers now live in `./fork-session-key.ts` (a leaf
 * module) so the `localStorage` read path in `storage.ts` can use the
 * same primitive without an import cycle. The re-exports below keep
 * the existing public API stable for every call site in `app.ts`,
 * `app-chat-tabs.ts`, and the test suite.
 */
import {
  PERSISTED_FORK_RE,
  canonicalizeSessionKeyForRehydrate,
  guardPersistedSessionKey,
  isPersistedFork,
} from "./fork-session-key.ts";

export {
  PERSISTED_FORK_RE,
  canonicalizeSessionKeyForRehydrate,
  guardPersistedSessionKey,
  isPersistedFork,
};

/**
 * Strip fork entries from a `chatOpenTabs` array, replacing each with
 * the canonical main key so tab positions are preserved. Empty slots
 * are also removed. Returns the input unchanged when no sanitisation
 * is needed, so we don't churn `applySettings` on every load.
 */
export function sanitizeChatOpenTabs(tabs: readonly string[] | null | undefined): string[] {
  if (!Array.isArray(tabs) || tabs.length === 0) {
    return [];
  }
  let dirty = false;
  const out: string[] = [];
  for (const raw of tabs) {
    if (typeof raw !== "string") {
      dirty = true;
      continue;
    }
    const trimmed = raw.trim();
    if (!trimmed) {
      dirty = true;
      continue;
    }
    if (isPersistedFork(trimmed)) {
      out.push("main");
      dirty = true;
    } else {
      out.push(trimmed);
    }
  }
  return dirty ? out : (tabs as string[]);
}

export function applySettings(host: SettingsHost, next: UiSettings) {
  // JAISIU-XXXX — guard both persisted session-key fields against fork
  // rehydration. `applySettings` is the only writer for
  // `settings.lastActiveSessionKey` (which seeds `host.applySessionKey`
  // below) and the canonical writer for `settings.sessionKey` outside
  // of `applySettingsFromUrl`. Both must be sanitised in one place
  // so every caller (chat tabs, reset, project create) gets the same
  // fork-stripping treatment.
  const sanitizedSessionKey = canonicalizeSessionKeyForRehydrate(next.sessionKey);
  const sanitizedLastActive = guardPersistedSessionKey(next.lastActiveSessionKey);
  const sanitizedTabs = sanitizeChatOpenTabs(next.chatOpenTabs);
  const normalized = {
    ...next,
    sessionKey: sanitizedSessionKey,
    lastActiveSessionKey: sanitizedLastActive || sanitizedSessionKey,
    chatOpenTabs: sanitizedTabs,
  };
  host.settings = normalized;
  saveSettings(normalized);
  if (next.theme !== host.theme) {
    host.theme = next.theme;
    applyResolvedTheme(host, resolveTheme(next.theme));
  }
  host.applySessionKey = host.settings.lastActiveSessionKey;
}

export function setLastActiveSessionKey(host: SettingsHost, next: string) {
  const trimmed = next.trim();
  if (!trimmed) {
    return;
  }
  if (host.settings.lastActiveSessionKey === trimmed) {
    return;
  }
  applySettings(host, { ...host.settings, lastActiveSessionKey: trimmed });
}

export function applySettingsFromUrl(host: SettingsHost) {
  if (!window.location.search && !window.location.hash) {
    return;
  }
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  const hashParams = new URLSearchParams(url.hash.startsWith("#") ? url.hash.slice(1) : url.hash);

  const tokenRaw = params.get("token") ?? hashParams.get("token");
  const passwordRaw = params.get("password") ?? hashParams.get("password");
  const sessionRaw = params.get("session") ?? hashParams.get("session");
  const gatewayUrlRaw = params.get("gatewayUrl") ?? hashParams.get("gatewayUrl");
  let shouldCleanUrl = false;

  if (tokenRaw != null) {
    const token = tokenRaw.trim();
    if (token && token !== host.settings.token) {
      applySettings(host, { ...host.settings, token });
    }
    params.delete("token");
    hashParams.delete("token");
    shouldCleanUrl = true;
  }

  if (passwordRaw != null) {
    const password = passwordRaw.trim();
    if (password) {
      (host as unknown as { password: string }).password = password;
    }
    params.delete("password");
    hashParams.delete("password");
    shouldCleanUrl = true;
  }

  if (sessionRaw != null) {
    const rawSession = sessionRaw.trim();
    if (rawSession) {
      // JAISIU-XXXX — strip forks from URL-supplied session params. A
      // bookmarked or shared link that points at `agent:main:fork:<uuid>`
      // would otherwise bypass the rehydrate guard at init and land the
      // operator in a sidetracked conversation.
      //
      // JAISIU-4497 — the fork guard is a REHYDRATE guard: it stops a
      // STALE persisted fork from resurrecting when the app boots. It
      // must NOT apply to an EXPLICIT navigation, because the whole
      // Fleet Control chat-history list is made of fork sessions
      // (`agent:<id>:fork:<uuid>`) — every "chat" the operator sees in
      // the sidebar is one. Collapsing the URL param to "main" made
      // every middle-clicked chat open the SAME session (the main one
      // at the bottom of the list) instead of the clicked one.
      //
      // The distinction that matters:
      //   - boot / rehydrate from PERSISTED state  → collapse the fork
      //     (a stale fork must not silently take over the session)
      //   - boot / rehydrate from an EXPLICIT URL    → honor the key
      //     as typed, because the operator (or the app's own
      //     "open in a new tab" affordance) asked for THIS session.
      //
      // `onPopState` already behaves this way (it assigns the raw key
      // to `host.sessionKey` and lets `applySettings` sanitize only the
      // persisted copy) — this makes the initial load agree with it.
      const session = canonicalizeSessionKeyForRehydrate(rawSession);
      const explicit = rawSession === session ? session : rawSession;
      host.sessionKey = explicit;
      applySettings(host, {
        ...host.settings,
        sessionKey: explicit,
        lastActiveSessionKey: explicit,
      });
    }
  }

  if (gatewayUrlRaw != null) {
    const gatewayUrl = gatewayUrlRaw.trim();
    if (gatewayUrl && gatewayUrl !== host.settings.gatewayUrl) {
      host.pendingGatewayUrl = gatewayUrl;
    }
    params.delete("gatewayUrl");
    hashParams.delete("gatewayUrl");
    shouldCleanUrl = true;
  }

  if (!shouldCleanUrl) {
    return;
  }
  url.search = params.toString();
  const nextHash = hashParams.toString();
  url.hash = nextHash ? `#${nextHash}` : "";
  window.history.replaceState({}, "", url.toString());
}

/**
 * Open the Local LLM console inside the unified LLM page.
 *
 * The old dedicated `local-llm` nav tab is gone; Local LLM now lives as a
 * sub-tab of the merged `LLM` page. Persist the sub-tab choice via
 * `settings.llmStatsTab = "local"` so it survives reloads, then navigate
 * to the unified LLM tab.
 */
export function openLocalLlmInFleet(host: SettingsHost) {
  applySettings(host, {
    ...host.settings,
    fleetChatMainTab: "chat",
    llmStatsTab: "local",
  });
  setTab(host, "llm");
  syncLocalLlmPolling(host as unknown as Parameters<typeof syncLocalLlmPolling>[0]);
  if (host.connected) {
    void refreshLocalLlm(host as unknown as Parameters<typeof refreshLocalLlm>[0]);
  }
}

export function setTab(host: SettingsHost, next: Tab) {
  if (host.tab !== next) {
    host.tab = next;
  }
  if (isFleetConsoleTab(next)) {
    host.chatHasAutoScrolled = false;
  }
  if (next === "logs") {
    startLogsPolling(host as unknown as Parameters<typeof startLogsPolling>[0]);
  } else {
    stopLogsPolling(host as unknown as Parameters<typeof stopLogsPolling>[0]);
  }
  if (next === "debug") {
    startDebugPolling(host as unknown as Parameters<typeof startDebugPolling>[0]);
  } else {
    stopDebugPolling(host as unknown as Parameters<typeof stopDebugPolling>[0]);
  }
  if (next === "employees") {
    startLiveSessionsPolling(host as unknown as Parameters<typeof startLiveSessionsPolling>[0]);
  } else {
    stopLiveSessionsPolling(host as unknown as Parameters<typeof stopLiveSessionsPolling>[0]);
  }
  if (next === "llm-stats" || next === "llm") {
    startLlmStatsCapacityPolling(
      host as unknown as Parameters<typeof startLlmStatsCapacityPolling>[0],
    );
  } else {
    stopLlmStatsCapacityPolling(
      host as unknown as Parameters<typeof stopLlmStatsCapacityPolling>[0],
    );
  }

  if (next === "agents") {
    startAgentsPolling(host as unknown as Parameters<typeof startAgentsPolling>[0]);
  } else {
    stopAgentsPolling(host as unknown as Parameters<typeof stopAgentsPolling>[0]);
  }
  syncFleetWorkPlanPolling(
    host as unknown as Parameters<typeof syncFleetWorkPlanPolling>[0],
    next === "agents" && host.agentsPanel === "workplan",
  );
  if (next === "instances") {
    startNodesPolling(host as unknown as Parameters<typeof startNodesPolling>[0]);
  } else {
    stopNodesPolling(host as unknown as Parameters<typeof stopNodesPolling>[0]);
  }
  // Subagents list drives the chat-composer sidebar; only useful while a
  // fleet-console tab is mounted.
  if (isFleetConsoleTab(next)) {
    startSubagentsPolling(host as unknown as Parameters<typeof startSubagentsPolling>[0]);
  } else {
    stopSubagentsPolling(host as unknown as Parameters<typeof stopSubagentsPolling>[0]);
  }
  if (isLocalLlmViewActive({ tab: next })) {
    startLocalLlmPolling(host as unknown as Parameters<typeof startLocalLlmPolling>[0]);
  } else {
    stopLocalLlmPolling(host as unknown as Parameters<typeof stopLocalLlmPolling>[0]);
  }
  syncChatPolling(host as unknown as Parameters<typeof syncChatPolling>[0]);
  // If we're navigating to the employees tab from elsewhere, clear any selected employee
  // unless we're already on a detail sub-route
  if (next === "employees" && !employeeIdFromPath(window.location.pathname, host.basePath)) {
    host.setSelectedEmployee?.(null);
  }

  if (next === "help") {
    syncHelpTopicWithLocation(host);
  }

  void refreshActiveTab(host);
  syncUrlWithTab(host, next, false);
}

export function syncHelpTopicWithLocation(host: SettingsHost) {
  if (typeof window === "undefined" || host.tab !== "help") {
    return;
  }
  host.helpTopic = helpTopicFromPath(window.location.pathname, host.basePath);
}

export function setHelpTopic(host: SettingsHost, topic: HelpTopicId) {
  host.helpTopic = topic;
  if (host.tab !== "help") {
    host.tab = "help";
  }
  syncUrlWithHelpTopic(host, topic, false);
}

export function syncUrlWithHelpTopic(host: SettingsHost, topic: HelpTopicId, replace: boolean) {
  if (typeof window === "undefined") {
    return;
  }
  const targetPath = normalizePath(pathForHelpTopic(topic, host.basePath));
  const url = new URL(window.location.href);
  url.searchParams.delete("session");
  url.pathname = targetPath;
  if (replace) {
    window.history.replaceState({}, "", url.toString());
  } else {
    window.history.pushState({}, "", url.toString());
  }
}

export function setEmployeeDetail(host: SettingsHost, employeeId: string | null) {
  const currentEmployeeId = employeeIdFromPath(window.location.pathname, host.basePath);
  if (employeeId === currentEmployeeId) {
    return;
  }

  if (employeeId) {
    host.tab = "employees";
    const url = new URL(window.location.href);
    url.pathname = pathForEmployeeDetail(employeeId, host.basePath);
    window.history.pushState({}, "", url.toString());
  } else {
    host.tab = "employees";
    const url = new URL(window.location.href);
    url.pathname = pathForTab("employees", host.basePath);
    window.history.pushState({}, "", url.toString());
  }

  void refreshActiveTab(host);
}

export function setTheme(host: SettingsHost, next: ThemeMode, context?: ThemeTransitionContext) {
  const applyTheme = () => {
    host.theme = next;
    applySettings(host, { ...host.settings, theme: next });
    applyResolvedTheme(host, resolveTheme(next));
  };
  startThemeTransition({
    nextTheme: next,
    applyTheme,
    context,
    currentTheme: host.theme,
  });
}

export async function refreshActiveTab(host: SettingsHost) {
  if (host.tab === "overview") {
    await loadOverview(host);
  }
  if (host.tab === "channels") {
    await loadChannelsTab(host);
  }
  if (host.tab === "instances") {
    await Promise.all([
      loadPresence(host as unknown as OpenClawApp),
      loadNodes(host as unknown as OpenClawApp),
      loadDevices(host as unknown as OpenClawApp),
      loadConfig(host as unknown as OpenClawApp),
      loadExecApprovals(host as unknown as OpenClawApp),
    ]);
  }
  if (host.tab === "sessions") {
    await loadSessions(host as unknown as OpenClawApp);
  }
  if (host.tab === "llm-stats" || host.tab === "llm") {
    const capacityHost = host as unknown as Parameters<typeof loadLlmStats>[0] &
      Parameters<typeof loadLocalResources>[0];
    await Promise.all([loadLlmStats(capacityHost), loadLocalResources(capacityHost)]);
  }
  if (isLocalLlmViewActive(host)) {
    await refreshLocalLlm(host as unknown as Parameters<typeof refreshLocalLlm>[0]);
  }
  if (host.tab === "cron") {
    await loadCron(host);
  }
  if (host.tab === "skills") {
    await loadSkills(host as unknown as OpenClawApp);
  }
  if (host.tab === "agents") {
    const agentsApp = host as unknown as OpenClawApp;
    await loadAgents(agentsApp);
    await loadConfig(agentsApp);
    hydrateFleetRolesFromConfig(agentsApp);
    if (agentsApp.agentsPageTab === "import") {
      void loadSessionsForAgentsImport(agentsApp);
    }
    const coercedAgentId = resolveAgentsTabAgentId(agentsApp);
    if (coercedAgentId && agentsApp.agentsSelectedId !== coercedAgentId) {
      agentsApp.agentsSelectedId = coercedAgentId;
    }
    const agentIds = agentsApp.agentsList?.agents?.map((entry) => entry.id) ?? [];
    if (agentIds.length > 0) {
      void loadAgentIdentities(agentsApp, agentIds);
    }
    const agentId = resolveAgentsTabAgentId(agentsApp);
    if (agentId) {
      void loadAgentIdentity(agentsApp, agentId);
      if (agentsApp.agentsPanel === "skills") {
        void loadAgentSkills(agentsApp, agentId);
      }
      if (agentsApp.agentsPanel === "channels") {
        void loadChannels(agentsApp, false);
      }
      if (agentsApp.agentsPanel === "cron") {
        void loadCron(host);
      }
      if (agentsApp.agentsPanel === "workplan" && agentId) {
        void loadFleetWorkPlans(agentsApp);
        void loadFleetWorkPlanBranches(agentsApp);
        if (agentsApp.workPlanSelectedId) {
          void loadFleetWorkPlanDetail(agentsApp, agentsApp.workPlanSelectedId);
        }
      }
    }
  }
  if (host.tab === "employees") {
    const app = host as unknown as OpenClawApp;
    await loadEmployeesData(app);
    await loadLiveSessions(app);
    const employeeId = employeeIdFromPath(window.location.pathname, host.basePath);
    if (employeeId) {
      const app = host as unknown as OpenClawApp;
      app.setSelectedEmployee(employeeId);
    }
  }
  if (host.tab === "workforce") {
    await Promise.all([
      loadEmployeesData(host as unknown as OpenClawApp),
      loadWorkforceUi(host as unknown as Parameters<typeof loadWorkforceUi>[0]),
    ]);
  }
  if (isFleetConsoleTab(host.tab)) {
    await refreshChat(host as unknown as Parameters<typeof refreshChat>[0]);
    void (
      host as unknown as { refreshChatWorkspace?: () => Promise<void> }
    ).refreshChatWorkspace?.();
    void (
      host as unknown as { refreshAgentFleetGraph?: () => Promise<void> }
    ).refreshAgentFleetGraph?.();
    scheduleChatScroll(
      host as unknown as Parameters<typeof scheduleChatScroll>[0],
      !host.chatHasAutoScrolled,
    );
  }
  if (host.tab === "config") {
    await loadConfigSchema(host as unknown as OpenClawApp);
    await loadConfig(host as unknown as OpenClawApp);
  }
  if (host.tab === "developer") {
    await loadDevBuildInfo(host as unknown as OpenClawApp);
  }
  if (host.tab === "debug") {
    await loadDebug(host as unknown as OpenClawApp);
    host.eventLog = host.eventLogBuffer;
  }
  if (host.tab === "logs") {
    host.logsAtBottom = true;
    await reloadLogs(host as unknown as import("./app.ts").OpenClawApp, { reset: true });
    scheduleLogsScroll(host as unknown as Parameters<typeof scheduleLogsScroll>[0], true);
  }
}

export function inferBasePath() {
  if (typeof window === "undefined") {
    return "";
  }
  const configured = window.__OPENCLAW_CONTROL_UI_BASE_PATH__;
  if (typeof configured === "string" && configured.trim()) {
    return normalizeBasePath(configured);
  }
  return inferBasePathFromPathname(window.location.pathname);
}

export function syncThemeWithSettings(host: SettingsHost) {
  host.theme = host.settings.theme ?? "system";
  applyResolvedTheme(host, resolveTheme(host.theme));
}

export function applyResolvedTheme(host: SettingsHost, resolved: ResolvedTheme) {
  host.themeResolved = resolved;
  if (typeof document === "undefined") {
    return;
  }
  const root = document.documentElement;
  root.dataset.theme = resolved;
  root.style.colorScheme = resolved;
}

export function attachThemeListener(host: SettingsHost) {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return;
  }
  host.themeMedia = window.matchMedia("(prefers-color-scheme: dark)");
  host.themeMediaHandler = (event) => {
    if (host.theme !== "system") {
      return;
    }
    applyResolvedTheme(host, event.matches ? "dark" : "light");
  };
  if (typeof host.themeMedia.addEventListener === "function") {
    host.themeMedia.addEventListener("change", host.themeMediaHandler);
    return;
  }
  const legacy = host.themeMedia as MediaQueryList & {
    addListener: (cb: (event: MediaQueryListEvent) => void) => void;
  };
  legacy.addListener(host.themeMediaHandler);
}

export function detachThemeListener(host: SettingsHost) {
  if (!host.themeMedia || !host.themeMediaHandler) {
    return;
  }
  if (typeof host.themeMedia.removeEventListener === "function") {
    host.themeMedia.removeEventListener("change", host.themeMediaHandler);
    return;
  }
  const legacy = host.themeMedia as MediaQueryList & {
    removeListener: (cb: (event: MediaQueryListEvent) => void) => void;
  };
  legacy.removeListener(host.themeMediaHandler);
  host.themeMedia = null;
  host.themeMediaHandler = null;
}

export function syncTabWithLocation(host: SettingsHost, replace: boolean) {
  if (typeof window === "undefined") {
    return;
  }
  const resolved = tabFromPath(window.location.pathname, host.basePath) ?? "chat";
  setTabFromRoute(host, resolved);
  if (resolved === "help") {
    syncHelpTopicWithLocation(host);
  }
  syncUrlWithTab(host, resolved, replace);
}

export function onPopState(host: SettingsHost) {
  if (typeof window === "undefined") {
    return;
  }
  const resolved = tabFromPath(window.location.pathname, host.basePath);
  if (!resolved) {
    return;
  }

  const url = new URL(window.location.href);
  const session = url.searchParams.get("session")?.trim();
  if (session) {
    host.sessionKey = session;
    applySettings(host, {
      ...host.settings,
      sessionKey: session,
      lastActiveSessionKey: session,
    });
  }

  if (resolved === "help") {
    syncHelpTopicWithLocation(host);
  }
  setTabFromRoute(host, resolved);
}

export function setTabFromRoute(host: SettingsHost, next: Tab) {
  if (host.tab !== next) {
    host.tab = next;
  }
  if (isFleetConsoleTab(next)) {
    host.chatHasAutoScrolled = false;
  }
  if (next === "logs") {
    startLogsPolling(host as unknown as Parameters<typeof startLogsPolling>[0]);
  } else {
    stopLogsPolling(host as unknown as Parameters<typeof stopLogsPolling>[0]);
  }
  if (next === "debug") {
    startDebugPolling(host as unknown as Parameters<typeof startDebugPolling>[0]);
  } else {
    stopDebugPolling(host as unknown as Parameters<typeof stopDebugPolling>[0]);
  }
  if (next === "employees") {
    startLiveSessionsPolling(host as unknown as Parameters<typeof startLiveSessionsPolling>[0]);
  } else {
    stopLiveSessionsPolling(host as unknown as Parameters<typeof stopLiveSessionsPolling>[0]);
  }
  if (next === "llm-stats" || next === "llm") {
    startLlmStatsCapacityPolling(
      host as unknown as Parameters<typeof startLlmStatsCapacityPolling>[0],
    );
  } else {
    stopLlmStatsCapacityPolling(
      host as unknown as Parameters<typeof stopLlmStatsCapacityPolling>[0],
    );
  }
  if (next === "agents") {
    startAgentsPolling(host as unknown as Parameters<typeof startAgentsPolling>[0]);
  } else {
    stopAgentsPolling(host as unknown as Parameters<typeof stopAgentsPolling>[0]);
  }
  syncFleetWorkPlanPolling(
    host as unknown as Parameters<typeof syncFleetWorkPlanPolling>[0],
    next === "agents" && host.agentsPanel === "workplan",
  );
  if (next === "instances") {
    startNodesPolling(host as unknown as Parameters<typeof startNodesPolling>[0]);
  } else {
    stopNodesPolling(host as unknown as Parameters<typeof stopNodesPolling>[0]);
  }
  if (isFleetConsoleTab(next)) {
    startSubagentsPolling(host as unknown as Parameters<typeof startSubagentsPolling>[0]);
  } else {
    stopSubagentsPolling(host as unknown as Parameters<typeof stopSubagentsPolling>[0]);
  }
  if (isLocalLlmViewActive(host)) {
    startLocalLlmPolling(host as unknown as Parameters<typeof startLocalLlmPolling>[0]);
  } else {
    stopLocalLlmPolling(host as unknown as Parameters<typeof stopLocalLlmPolling>[0]);
  }
  syncChatPolling(host as unknown as Parameters<typeof syncChatPolling>[0]);
  if (host.connected) {
    void refreshActiveTab(host);
  }
}

export function syncUrlWithTab(host: SettingsHost, tab: Tab, replace: boolean) {
  if (typeof window === "undefined") {
    return;
  }
  const targetPath =
    tab === "help"
      ? normalizePath(pathForHelpTopic(host.helpTopic, host.basePath))
      : normalizePath(pathForTab(tab, host.basePath));
  const currentPath = normalizePath(window.location.pathname);
  const url = new URL(window.location.href);

  const sessionForUrl =
    host.sessionKey?.trim() ||
    host.settings.sessionKey?.trim() ||
    host.settings.lastActiveSessionKey?.trim() ||
    "";
  if (isFleetConsoleTab(tab) && sessionForUrl) {
    url.searchParams.set("session", sessionForUrl);
  } else if (!isFleetConsoleTab(tab)) {
    url.searchParams.delete("session");
  }

  if (currentPath !== targetPath) {
    url.pathname = targetPath;
  }

  if (replace) {
    window.history.replaceState({}, "", url.toString());
  } else {
    window.history.pushState({}, "", url.toString());
  }
}

export function syncUrlWithSessionKey(host: SettingsHost, sessionKey: string, replace: boolean) {
  if (typeof window === "undefined") {
    return;
  }
  const trimmed = sessionKey.trim();
  if (!trimmed) {
    return;
  }
  const url = new URL(window.location.href);
  url.searchParams.set("session", trimmed);
  const chatPath = normalizePath(pathForTab("chat", host.basePath));
  if (isFleetConsoleTab(host.tab) && normalizePath(url.pathname) !== chatPath) {
    url.pathname = chatPath;
  }
  if (replace) {
    window.history.replaceState({}, "", url.toString());
  } else {
    window.history.pushState({}, "", url.toString());
  }
}

export async function loadOverview(host: SettingsHost) {
  await Promise.all([
    loadChannels(host as unknown as OpenClawApp, false),
    loadPresence(host as unknown as OpenClawApp),
    loadSessions(host as unknown as OpenClawApp),
    loadCronStatus(host as unknown as OpenClawApp),
    loadDebug(host as unknown as OpenClawApp),
    loadGoogleDriveStatus(host as unknown as GoogleDriveHost),
  ]);
}

export async function loadChannelsTab(host: SettingsHost) {
  await Promise.all([
    loadChannels(host as unknown as OpenClawApp, true),
    loadConfigSchema(host as unknown as OpenClawApp),
    loadConfig(host as unknown as OpenClawApp),
  ]);
}

export async function loadCron(host: SettingsHost) {
  await Promise.all([
    loadChannels(host as unknown as OpenClawApp, false),
    loadCronStatus(host as unknown as OpenClawApp),
    loadCronJobs(host as unknown as OpenClawApp),
  ]);
}
